/**
 * AutoTopupCard — owner-only widget inside Plan & Billing that lets a
 * seller arm an automatic wallet topup from a card on file when the
 * balance is about to dip below a threshold. The cycle engine reads
 * these settings on every charge attempt — if the wallet is short and
 * auto-topup is armed, it pulls funds from the card BEFORE flipping
 * the tenant into grace.
 *
 * In preview the card charge is mocked (writes a `wallet_topup_auto`
 * txn). In production this routes through TabaPay.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { CreditCard, Zap, Loader2, ShieldCheck } from "lucide-react";


export default function AutoTopupCard() {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [enabled, setEnabled]     = useState(false);
  const [thresholdUSD, setThresh] = useState("100");
  const [amountUSD, setAmount]    = useState("300");
  const [cardId, setCardId]       = useState("");
  const [cards, setCards]         = useState([]);
  const [lastTopup, setLastTopup] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/billing/auto-topup");
        setEnabled(!!r.data.settings.enabled);
        setThresh(((r.data.settings.threshold_cents || 10000) / 100).toString());
        setAmount(((r.data.settings.amount_cents || 30000) / 100).toString());
        setCardId(r.data.settings.card_id || "");
        setCards((r.data.cards || []).filter(c => !c.frozen_at));
        setLastTopup({
          at: r.data.settings.last_topup_at,
          cents: r.data.settings.last_topup_cents,
        });
      } catch {
        // silent — panel just renders empty
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/billing/auto-topup", {
        enabled,
        threshold_cents: Math.round(parseFloat(thresholdUSD) * 100),
        amount_cents:    Math.round(parseFloat(amountUSD) * 100),
        card_id:         cardId || null,
      });
      toast.success(enabled ? "Auto-topup armed" : "Auto-topup disabled");
      setLastTopup({
        at: r.data.last_topup_at,
        cents: r.data.last_topup_cents,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-background p-5 flex items-center gap-2 text-foreground/40 text-sm" data-testid="auto-topup-loading">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading auto-topup…
      </div>
    );
  }

  return (
    <div className="rounded-2xl border bg-background overflow-hidden" data-testid="auto-topup-card">
      <div className="px-5 pt-5 pb-3 border-b border-border flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center shrink-0">
          <Zap className="w-4 h-4"/>
        </div>
        <div className="flex-1">
          <div className="display text-lg tracking-tight">Auto-topup from card on file</div>
          <p className="text-[12.5px] text-foreground/55 mt-0.5">
            When your wallet drops below the threshold, we pull funds from your saved card before the next SaaS charge so you never accidentally trip into grace mode.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="auto-topup-toggle"/>
      </div>

      {enabled && (
        <div className="px-5 py-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Threshold</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40">$</span>
              <Input type="number" min="10" max="1000" value={thresholdUSD} onChange={e => setThresh(e.target.value)} className="pl-7" data-testid="auto-topup-threshold"/>
            </div>
            <div className="text-[11px] text-foreground/50 mt-1">Trigger when wallet ≤ this amount</div>
          </div>
          <div>
            <label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Topup amount</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40">$</span>
              <Input type="number" min="10" max="5000" value={amountUSD} onChange={e => setAmount(e.target.value)} className="pl-7" data-testid="auto-topup-amount"/>
            </div>
            <div className="text-[11px] text-foreground/50 mt-1">Pull this much each time</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] mono uppercase tracking-widest text-foreground/55">Card on file</label>
            {cards.length === 0 ? (
              <div className="mt-2 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <CreditCard className="w-4 h-4 mt-0.5 shrink-0"/>
                <div>
                  No active cards on file. Add one in <a href="#payment-billing" className="underline">Payment &amp; billing</a> first.
                </div>
              </div>
            ) : (
              <Select value={cardId} onValueChange={setCardId}>
                <SelectTrigger className="mt-1" data-testid="auto-topup-card-select">
                  <SelectValue placeholder="Pick a card…"/>
                </SelectTrigger>
                <SelectContent>
                  {cards.map(c => (
                    <SelectItem key={c.id} value={c.id} data-testid={`auto-topup-card-opt-${c.last4}`}>
                      <span className="inline-flex items-center gap-2">
                        <CreditCard className="w-3.5 h-3.5"/>
                        {(c.brand || "card").toUpperCase()} •• {c.last4} {c.exp && `· exp ${c.exp}`}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {lastTopup?.at && (
        <div className="px-5 pb-4 -mt-1 text-[11.5px] text-foreground/55">
          Last auto-topup: ${((lastTopup.cents || 0) / 100).toFixed(2)} on {new Date(lastTopup.at).toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}
        </div>
      )}

      <div className="px-5 pb-5 pt-1 flex items-center justify-between gap-3">
        <div className="text-[11px] text-foreground/50 inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3"/> Owner-only · mocked in preview · routes through TabaPay in prod.
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="auto-topup-save">
          {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
          {enabled ? "Save settings" : "Save (disabled)"}
        </Button>
      </div>
    </div>
  );
}
